import React, { useEffect, useMemo } from 'react'
import './InputTarifaAdicional.css'
import { Controller, useFormContext } from 'react-hook-form'
import { ExtensionEstanciaValues } from '../../../../EstensionEstancia-types'
import MultipleSelectDropdown from 'src/shared/components/forms/MultipleSelectDropdown/MultipleSelectDropdown'
import useDateRange from '../../../../hooks/useDateRange'
import { useDate } from 'src/shared/hooks/useDate'
import { EstatusGrupoTarifa, EstatusTarifa, GrupoTarifa, Habitacion, ModalidadGrupoTarifa, TipoAlojamientoGrupoTarifa, TiposAlojamientos, TipoTarifa, useGruposTarifasRenovacionEstanciaQuery } from 'src/gql/schema'
import { useProfile } from 'src/shared/hooks/useProfile'
import { useCosts } from '../../../../context/CostContext'
import { formatDateShort } from './formatDateShort'
import { sum, times } from 'src/shared/helpers/calculator'
import { useFormatDate } from 'src/shared/hooks/useFormatDate'

const InputTarifaAdicional = ({ tipo_habitacion_id, tipo_alojamiento, habitacion, loading }: {
    tipo_habitacion_id?: string,
    tipo_alojamiento?: TiposAlojamientos
    habitacion?: Habitacion
    loading?: boolean
}) => {

    const { nowHotel, setStartDate } = useFormatDate()
    const { UTCStringToLocalDate, diffDays, areSameDay } = useDate()

    const fechaCondensada = useMemo(() =>
        habitacion?.ultima_renta?.fecha_condensada
            ? UTCStringToLocalDate(habitacion.ultima_renta.fecha_condensada)
            : nowHotel
    , [habitacion, nowHotel])
    const { hotel_id } = useProfile()

    const { setGruposTarifasAdicionalesSelected, setTarifaTemporadaAdicionalUltimoDia, gruposTarifasAdicionalesSelected } = useCosts()

    const { data: gruposTarifas, loading: loadingTarifas } = useGruposTarifasRenovacionEstanciaQuery({
        variables: {
            hotel_id,
            tipo_habitacion_id: tipo_habitacion_id!,
            tipo_alojamiento: tipo_alojamiento as unknown as TipoAlojamientoGrupoTarifa,
            tipos_tarifa: [TipoTarifa.Renta, TipoTarifa.RentaReserva],
            modalidades: [ModalidadGrupoTarifa.Adicional],
            modalidad: ModalidadGrupoTarifa.Adicional
        },
        skip: !tipo_habitacion_id || !tipo_alojamiento
    })

    const {
        control,
        watch,
        setValue
    } = useFormContext<ExtensionEstanciaValues>()

    const nochesExtra = watch("principal.nochesExtraCount")
    const tarifasAdicionalesSelected = watch("principal.tarifasAdicionalesIds")
    const personasExtra = watch("personas.extraCount")
    const horasExtra = watch("principal.horasExtraCount")

    const { endDate, startDate } = useDateRange({ startDate: fechaCondensada, daysCount: nochesExtra || 0 })

    const isMotel = tipo_alojamiento === TiposAlojamientos.Motel

    useEffect(() => {
        // guardar en memoria los grupos tarifas seleccionados obteniendo solo las tarifas elegidas para mostrar la data en el resumen
        setGruposTarifasAdicionalesSelected(
            ((gruposTarifas?.grupos_tarifas.filter(
                gp => gp.tarifas?.find(tarifa =>
                    tarifasAdicionalesSelected?.includes(tarifa.tarifa_id))) as GrupoTarifa[]) || [])
                .map(gp => ({
                    ...gp,
                    tarifas: gp.tarifas?.filter(tarifa =>
                        tarifasAdicionalesSelected?.includes(tarifa.tarifa_id)).map(tarifa => ({
                        ...tarifa,
                        costo_total: sum([
                            times((tarifa.costo_hospedaje_extra || 0), (nochesExtra || 0)),
                            (areSameDay(setStartDate(tarifa?.fecha_final), endDate)) ?
                            times((tarifa.costo_hora_extra || 0), (horasExtra || 0)) : 0,
                            times(times((tarifa.costo_persona_extra || 0), (nochesExtra || 1)), (personasExtra || 0))
                        ]),
                        costo_total_hospedajes_extra: times((tarifa.costo_hospedaje_extra || 0), (nochesExtra || 0)),
                        costo_total_horas_extra: 
                            (areSameDay(setStartDate(tarifa?.fecha_final), endDate)) ?
                        times((tarifa.costo_hora_extra || 0), (horasExtra || 0)) : 0,
                        costo_total_personas_extra: times(times((tarifa.costo_persona_extra || 0), (nochesExtra || 1)), (personasExtra || 0))
                    }))
                })))
    }, [gruposTarifas, tarifasAdicionalesSelected, nochesExtra, personasExtra, horasExtra])

    // valor inicial al input de tarifa y reset del input al cambiar de noches porque no se sabe qué tarifa va a aplicar o no al momento de agregar o quitar un día
    useEffect(() => {
        if (isMotel) return
        setValue("principal.tarifasAdicionalesIds", [])
    }, [nochesExtra, isMotel])

    useEffect(() => {
        setTarifaTemporadaAdicionalUltimoDia(
            gruposTarifasAdicionalesSelected
                .flatMap(gp => (gp.tarifas || []).map(t => ({ t, gp })))
                .find(({ t, gp }) => areSameDay(setStartDate(t?.fecha_final), endDate) && gp.modalidad !== ModalidadGrupoTarifa.Base)
                ?.t || null
        )
    }, [gruposTarifasAdicionalesSelected])

    const tarifas = gruposTarifas?.grupos_tarifas
        .filter(t => t.estatus === EstatusGrupoTarifa.Activa)
        .flatMap((gp) => gp.tarifas?.filter(tarifa =>
            tarifa.estatus === EstatusTarifa.Activa &&
            setStartDate(gp.fecha_inicio) < endDate &&
            setStartDate(gp.fecha_fin) >= startDate
        )) || []

    return !!tarifas.length && (
        <Controller
            control={control}
            name='principal.tarifasAdicionalesIds'
            render={({ field: { onChange, value } }) =>
                <MultipleSelectDropdown
                    icon='dollarCircle'
                    abbreviateLabel
                    disabled={loadingTarifas || loading}
                    abbreviateLabelText='tarifa(s)'
                    label='Tarifas adicionales (opcional)'
                    onChange={(val, lastSelected) => {
                        if (isMotel) {
                            const isSelecting = lastSelected !== undefined && val.includes(lastSelected)
                            onChange(isSelecting ? [lastSelected] : [])
                        } else {
                            onChange(val)
                        }
                    }}
                    closeOnSelect={isMotel}
                    value={value}
                    containerClassName='renovacion-estancia__tarifa__dropdown'
                    options={
                        gruposTarifas?.grupos_tarifas.filter(t => t.estatus === EstatusGrupoTarifa.Activa).flatMap((gp) => gp.tarifas?.filter(tarifa =>
                            tarifa.estatus === EstatusTarifa.Activa &&
                            setStartDate(gp.fecha_inicio) < endDate &&
                            setStartDate(gp.fecha_fin) >= startDate
                        ).map((tarifa, index) => ({
                            // las tarifas bases no tienen fecha de vencimiento
                            dropdownLabel: (!isMotel && gp.modalidad !== ModalidadGrupoTarifa.Base) ? <p className='input-tarifa-base__option__name'>{tarifa.grupo_tarifa?.nombre || "-"} <span className='input-tarifa-base__option__day'>{((nochesExtra || 0) > 1 || (diffDays(setStartDate(gp.fecha_inicio), setStartDate(gp.fecha_fin)) > 1)) ? `(${formatDateShort(setStartDate(gp.fecha_inicio))} - ${formatDateShort(setStartDate(gp.fecha_fin))})` : `(${formatDateShort(setStartDate(gp.fecha_inicio))})`}</span></p> : undefined,
                            label: isMotel ? `${tarifa.grupo_tarifa?.nombre || "-"} | ${tarifa.grupo_tarifa?.duracion || "-"} horas` : tarifa.grupo_tarifa?.nombre || "-",
                            value: tarifa.tarifa_id,
                            withCheckbox: !isMotel,
                            withCheckIcon: isMotel,
                        })) || []) || []
                    }
                    placeholder='Selecciona una opción'
                />
            }
        />
    )
}

export default InputTarifaAdicional